// HostelOverview.jsx — read-only Hostel visibility for Admin and Finance.
// All actual room/allocation/fine management happens on the Hostel Member's
// own portal (HostelOpsPages.jsx) — this is oversight only.
import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  HomeModernIcon, UserGroupIcon, ExclamationTriangleIcon, CheckCircleIcon, ClockIcon,
} from '@heroicons/react/24/outline';

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .ho * { box-sizing:border-box; }
  .ho { font-family:'Sora',sans-serif; color:#1a1d2e; animation:hoFade .4s ease both; }
  @keyframes hoFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  .ho-title { font-size:1.35rem; font-weight:700; letter-spacing:-.02em; color:#0f1123; }
  .ho-sub   { font-size:.76rem; color:#9094a8; margin-top:3px; margin-bottom:1.4rem; }
  .stat-grid { display:grid; grid-template-columns:repeat(5,1fr); gap:1rem; margin-bottom:1.4rem; }
  @media (max-width:1000px) { .stat-grid { grid-template-columns:repeat(3,1fr); } }
  @media (max-width:640px)  { .stat-grid { grid-template-columns:repeat(2,1fr); } }
  .stat-card { background:white; border-radius:14px; padding:1rem 1.2rem; border:1px solid #e8ebf4; }
  .sc-icon  { width:34px; height:34px; border-radius:10px; display:flex; align-items:center; justify-content:center; margin-bottom:.7rem; }
  .sc-icon svg { width:16px; height:16px; stroke-width:1.8; }
  .sc-icon.pink  { background:#fdf2f8; color:#ec4899; }
  .sc-icon.blue  { background:#eef2ff; color:#3b6ef8; }
  .sc-icon.green { background:#ecfdf5; color:#10c98e; }
  .sc-icon.amber { background:#fffbeb; color:#f59e0b; }
  .sc-label { font-size:.67rem; font-weight:600; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .sc-value { font-size:1.3rem; font-weight:700; color:#0f1123; font-family:'JetBrains Mono',monospace; line-height:1.2; margin-top:2px; }
  .tabs { display:flex; gap:6px; flex-wrap:wrap; margin-bottom:1.2rem; }
  .tab  { padding:6px 14px; border-radius:99px; border:1.5px solid #e0e4f0; background:white; font-family:'Sora',sans-serif; font-size:.75rem; font-weight:600; cursor:pointer; color:#6b7194; }
  .tab.active { background:#ec4899; color:white; border-color:#ec4899; }
  .panel { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; }
  .tbl { width:100%; border-collapse:collapse; }
  .tbl thead { background:#fafbff; border-bottom:1px solid #edf0f9; }
  .tbl th { padding:10px 14px; text-align:center; font-size:.65rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; white-space:nowrap; }
  .tbl td { padding:10px 14px; font-size:.8rem; text-align:center; vertical-align:middle; border-bottom:1px solid #f3f5fb; }
  .empty { text-align:center; padding:3rem 2rem; color:#9094a8; font-size:.82rem; }
  .loading { display:flex; align-items:center; justify-content:center; height:180px; gap:10px; color:#9094a8; }
  .spinner { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#ec4899; border-radius:50%; animation:spin .7s linear infinite; }
  @keyframes spin { to{transform:rotate(360deg);} }
  .pill { font-size:.66rem; font-weight:600; padding:3px 9px; border-radius:99px; display:inline-flex; white-space:nowrap; }
  .pill-active{background:#ecfdf5;color:#10c98e;} .pill-vacated{background:#f0f2f7;color:#9094a8;}
  .pill-pending{background:#fffbeb;color:#f59e0b;} .pill-applied{background:#ecfdf5;color:#10c98e;}
  .pill-in{background:#ecfdf5;color:#10c98e;} .pill-out{background:#fef2f2;color:#ef4444;}
`;

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US',{day:'numeric',month:'short',year:'numeric'}) : '—';
// "Grade 10 — A" (dash skipped when there is no section)
const classLabel = (name, section) => (name && section) ? `${name} — ${section}` : (name || section || '—');
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
// "2026-09-12 16:54:00" -> "12 Sep 2026, 4:54 PM" (parsed by hand: no timezone shift)
const fmtDateTime = (v) => {
  if (!v) return '—';
  const m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
  if (!m) return fmtDate(v);
  const h = Number(m[4]);
  return `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]} ${m[1]}, ${h % 12 || 12}:${m[5]} ${h >= 12 ? 'PM' : 'AM'}`;
};
// Stay date+time from the event-log datetime, falling back to the plain date.
const fmtStay = (at, date) => at ? fmtDateTime(at) : fmtDate(date);
// Tab key -> endpoint path under /:role/hostel
const TAB_PATH = { rooms: 'rooms', allocations: 'allocations', fines: 'fines', logs: 'allocation-logs' };

const HostelOverview = ({ role = 'admin' }) => {
  const base = `/${role}/hostel`;
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState('allocations');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`${base}/overview`).then(r => setStats(r.data)).catch(()=>{});
  }, [base]);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`${base}/${TAB_PATH[tab]}`).then(r => setRows(r.data||[])).catch(()=>toast.error('Failed to load')).finally(()=>setLoading(false));
  }, [base, tab]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="ho"><style>{css}</style>
      <div className="ho-title">Hostel</div>
      <div className="ho-sub">Read-only oversight — rooms, allocations and fines are managed by your Hostel staff.</div>

      {stats && (
        <div className="stat-grid">
          <div className="stat-card"><div className="sc-icon pink"><HomeModernIcon/></div><div className="sc-label">Rooms / Capacity</div><div className="sc-value">{stats.totalRooms} / {stats.totalCapacity}</div></div>
          <div className="stat-card"><div className="sc-icon blue"><UserGroupIcon/></div><div className="sc-label">Occupied</div><div className="sc-value">{stats.occupied}</div></div>
          <div className="stat-card"><div className="sc-icon green"><CheckCircleIcon/></div><div className="sc-label">Vacant</div><div className="sc-value">{stats.vacant}</div></div>
          <div className="stat-card"><div className="sc-icon amber"><ClockIcon/></div><div className="sc-label">Pending Fines</div><div className="sc-value">{stats.pendingFines}</div></div>
          <div className="stat-card"><div className="sc-icon amber"><ExclamationTriangleIcon/></div><div className="sc-label">Fines Total</div><div className="sc-value">Rs. {Number(stats.finesTotal).toLocaleString()}</div></div>
        </div>
      )}

      <div className="tabs">
        <button className={`tab ${tab==='allocations'?'active':''}`} onClick={()=>setTab('allocations')}>Allocations</button>
        <button className={`tab ${tab==='fines'?'active':''}`} onClick={()=>setTab('fines')}>Fines</button>
        <button className={`tab ${tab==='logs'?'active':''}`} onClick={()=>setTab('logs')}>Logs</button>
        <button className={`tab ${tab==='rooms'?'active':''}`} onClick={()=>setTab('rooms')}>Rooms</button>
      </div>

      <div className="panel">
        {loading ? <div className="loading"><div className="spinner"/>Loading…</div>
        : rows.length===0 ? <div className="empty">Nothing here yet.</div>
        : tab === 'rooms' ? (
          <div style={{overflowX:'auto'}}>
            <table className="tbl">
              <thead><tr><th>Room No.</th><th>Block</th><th>Floor</th><th>Occupancy</th><th>Monthly Fee</th><th>Status</th></tr></thead>
              <tbody>{rows.map(r => (
                <tr key={r._id}><td style={{fontWeight:600}}>{r.roomNo}</td><td>{r.block||'—'}</td><td>{r.floor||'—'}</td>
                  <td style={{fontFamily:"'JetBrains Mono',monospace",fontWeight:700}}>{r.occupied}/{r.capacity}</td>
                  <td>{r.monthlyFee > 0 ? `Rs. ${Number(r.monthlyFee).toLocaleString()}` : '—'}</td><td><span className={`pill pill-${r.status==='active'?'active':'vacated'}`}>{r.status}</span></td></tr>
              ))}</tbody>
            </table>
          </div>
        ) : tab === 'allocations' ? (
          <div style={{overflowX:'auto'}}>
            <table className="tbl">
              <thead><tr><th>Room</th><th>Student</th><th>Class</th><th>Check-In</th><th>Check-Out</th><th>Status</th></tr></thead>
              <tbody>{rows.map(a => (
                <tr key={a._id}><td style={{fontWeight:600}}>{a.roomNo}</td><td>{a.studentName}</td><td>{classLabel(a.className, a.section)}</td>
                  <td style={{whiteSpace:'nowrap'}}>{fmtStay(a.checkInAt, a.checkInDate)}</td><td style={{whiteSpace:'nowrap'}}>{a.checkOutAt || a.checkOutDate ? fmtStay(a.checkOutAt, a.checkOutDate) : '—'}</td><td><span className={`pill pill-${a.status}`}>{a.status}</span></td></tr>
              ))}</tbody>
            </table>
          </div>
        ) : tab === 'logs' ? (
          <div style={{overflowX:'auto'}}>
            <table className="tbl">
              <thead><tr><th>Date &amp; Time</th><th>Student</th><th>Class</th><th>Room</th><th>Event</th></tr></thead>
              <tbody>{rows.map(l => (
                <tr key={l._id}><td style={{whiteSpace:'nowrap'}}>{fmtDateTime(l.occurredAt)}</td><td style={{fontWeight:600}}>{l.studentName}</td><td>{classLabel(l.className, l.section)}</td>
                  <td style={{fontWeight:600}}>{l.roomNo}</td>
                  <td>{l.action === 'check_in' ? <span className="pill pill-in">Check-In</span> : <span className="pill pill-out">Check-Out</span>}</td></tr>
              ))}</tbody>
            </table>
          </div>
        ) : (
          <div style={{overflowX:'auto'}}>
            <table className="tbl">
              <thead><tr><th>Student</th><th>Class</th><th>Reason</th><th>Amount</th><th>Month</th><th>Status</th></tr></thead>
              <tbody>{rows.map(f => (
                <tr key={f._id}><td style={{fontWeight:600}}>{f.studentName}</td><td>{classLabel(f.className, f.section)}</td><td>{f.reason}</td>
                  <td style={{fontFamily:"'JetBrains Mono',monospace",fontWeight:700}}>Rs. {Number(f.amount).toLocaleString()}</td><td>{f.month}</td>
                  <td><span className={`pill pill-${f.status}`}>{f.status==='applied'?'Applied':'Pending'}</span></td></tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default HostelOverview;
